import { useEffect, useState } from 'react';
import { managerApi } from '../../lib/api';
import { PUNCH_TYPES } from '../../lib/constants';
import { todayStr, fmtDateBR, fmtHM } from '../../lib/format';
import { useModal } from '../../components/ModalProvider';
import { ErrorModalBody, errorMessage } from '../../components/ErrorModalBody';

const TIPO_HORARIO_LABELS: Record<string, string> = {
  esperado: 'Esperado (atual) — bate no horário combinado, com tolerância e justificativa se fugir dela',
  turno: 'Por turno — horário calculado a partir do turno do funcionário vira o registro oficial',
  manual: 'Manual (em construção) — o gestor define os horários um a um, dia a dia',
};

export function ConfigTab() {
  const { showModal, closeModal } = useModal();
  const [cfg, setCfg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tol, setTol] = useState('10');
  const [camarotesText, setCamarotesText] = useState('');
  const [termo, setTermo] = useState('');
  const [email, setEmail] = useState('');
  const [passwords, setPasswords] = useState<{ id: string; criadoEm: string }[]>([]);
  const [newPassword, setNewPassword] = useState('');
  const [diurnoEntrada, setDiurnoEntrada] = useState('09:00');
  const [noturnoEntrada, setNoturnoEntrada] = useState('18:00');
  const [vtValor, setVtValor] = useState('');
  const [vtData, setVtData] = useState('');
  const [employees, setEmployees] = useState<{ id: string; nome: string; camarote: string }[]>([]);
  const [modoExclusao, setModoExclusao] = useState<'todos' | 'selecionar'>('selecionar');
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [senhaExcluir, setSenhaExcluir] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [excluindo, setExcluindo] = useState(false);
  const [erroExclusao, setErroExclusao] = useState('');

  async function reload() {
    const [res, pwRes, empRes] = await Promise.all([managerApi.getConfig(), managerApi.listManagerPasswords(), managerApi.listEmployees()]);
    setCfg(res.config);
    setTol(String(res.config.toleranciaMin));
    setCamarotesText(res.config.camarotes.join('\n'));
    setTermo(res.config.termoTexto);
    setEmail(res.config.managerEmail || '');
    setDiurnoEntrada(res.config.turnoDiurno?.entrada || '09:00');
    setNoturnoEntrada(res.config.turnoNoturno?.entrada || '18:00');
    setVtValor(res.config.vtValorPadrao || '');
    setVtData(res.config.vtDataPadrao || '');
    setPasswords(pwRes.passwords);
    setEmployees(empRes.employees);
    setSelecionados((prev) => new Set([...prev].filter((id) => empRes.employees.some((e: any) => e.id === id))));
  }
  useEffect(() => { setLoading(true); reload().finally(() => setLoading(false)); }, []);

  async function saveTolerance() {
    await managerApi.updateConfig({ toleranciaMin: Number(tol) || 10 });
    alert('Tolerância salva.');
  }
  async function saveCamarotes() {
    const camarotes = camarotesText.split('\n').map((s) => s.trim()).filter(Boolean);
    await managerApi.updateConfig({ camarotes });
    await reload();
  }
  async function saveTermo() {
    await managerApi.updateConfig({ termoTexto: termo.trim() });
    alert('Termo salvo. Funcionários que já aceitaram a versão anterior não precisarão aceitar de novo — avise se quiser exigir novo aceite.');
  }
  async function saveEmail() {
    await managerApi.updateConfig({ managerEmail: email.trim() });
    alert('E-mail salvo.');
  }
  async function saveVt() {
    await managerApi.updateConfig({ vtValorPadrao: vtValor.trim(), vtDataPadrao: vtData || null });
    alert('Padrão de vale-transporte salvo.');
  }

  async function addPassword() {
    if (!newPassword.trim()) { alert('Digite a nova senha.'); return; }
    try {
      await managerApi.addManagerPassword(newPassword.trim());
      setNewPassword('');
      await reload();
    } catch (err) {
      alert(errorMessage(err));
    }
  }
  async function removePassword(id: string) {
    if (!confirm('Remover esta senha mestre? Quem a usa não conseguirá mais entrar com ela.')) return;
    try {
      await managerApi.removeManagerPassword(id);
      await reload();
    } catch (err) {
      alert(errorMessage(err));
    }
  }

  function askScheduleModePassword(novoTipo: string) {
    if (novoTipo === cfg.tipoHorario) return;
    const Body = () => {
      const [senha, setSenha] = useState('');
      const [err, setErr] = useState('');
      const [busy, setBusy] = useState(false);
      async function confirmar() {
        setBusy(true);
        setErr('');
        try {
          await managerApi.updateConfig({ tipoHorario: novoTipo, senhaHorario: senha });
          closeModal();
          await reload();
        } catch (e) {
          setErr(errorMessage(e));
        } finally {
          setBusy(false);
        }
      }
      return (
        <>
          <h2>Trocar tipo de horário</h2>
          <p className="hint">Essa troca exige a senha especial de horário.</p>
          <label>Senha</label>
          <input type="password" value={senha} onChange={(e) => setSenha(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') confirmar(); }} />
          {err && <div className="err">{err}</div>}
          <div className="row" style={{ marginTop: 14 }}>
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={closeModal}>Cancelar</button>
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy} onClick={confirmar}>Confirmar</button>
          </div>
        </>
      );
    };
    showModal(<Body />);
  }

  async function saveTurnoAncora(turno: 'diurno' | 'noturno') {
    const entrada = turno === 'diurno' ? diurnoEntrada : noturnoEntrada;
    await managerApi.updateConfig(turno === 'diurno' ? { turnoDiurno: { entrada } } : { turnoNoturno: { entrada } });
    alert('Âncora do turno salva.');
  }

  async function previewTurno(turno: 'diurno' | 'noturno') {
    try {
      const res = await managerApi.previewTurno(turno);
      showModal(<>
        <h2>Prévia do turno {turno === 'diurno' ? 'diurno' : 'noturno'}</h2>
        <p className="hint">Horários que vão ser gravados como oficiais para quem tiver esse turno, quando o modo "Por turno" estiver ativo.</p>
        {PUNCH_TYPES.map((p) => (
          <div className="kv" key={p.key}><span>{p.label}</span><span className="mono">{res.horarios[p.key]}</span></div>
        ))}
        <button className="btn btn-ghost btn-block" style={{ marginTop: 14 }} onClick={closeModal}>Fechar</button>
      </>);
    } catch (err) {
      showModal(<ErrorModalBody error={err} onClose={closeModal} />);
    }
  }

  function toggleSelecionado(id: string) {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleTodosSelecionados() {
    setSelecionados((prev) => prev.size === employees.length ? new Set() : new Set(employees.map((e) => e.id)));
  }

  const qtdAlvo = modoExclusao === 'todos' ? employees.length : selecionados.size;

  async function excluirFuncionarios() {
    setErroExclusao('');
    if (qtdAlvo === 0) { setErroExclusao('Nenhum funcionário selecionado.'); return; }
    if (confirmText.trim().toUpperCase() !== 'EXCLUIR') { setErroExclusao('Digite EXCLUIR (em maiúsculas) para confirmar.'); return; }
    if (!senhaExcluir.trim()) { setErroExclusao('Digite a senha.'); return; }
    if (!confirm(`Tem certeza? Isso vai apagar ${qtdAlvo} funcionário(s) e TODOS os registros deles (ponto, alimentação, recebimentos, documentos assinados, fotos). Não pode ser desfeito.`)) return;
    setExcluindo(true);
    try {
      const res = await managerApi.deleteEmployees(senhaExcluir.trim(), modoExclusao === 'todos' ? { todos: true } : { ids: [...selecionados] });
      alert(`${res.excluidos} funcionário(s) excluído(s).`);
      setSenhaExcluir('');
      setConfirmText('');
      setSelecionados(new Set());
      await reload();
    } catch (err) {
      setErroExclusao(errorMessage(err));
    } finally {
      setExcluindo(false);
    }
  }

  async function sendSummaryEmail() {
    const date = todayStr();
    const [pontoLog, alimLog] = await Promise.all([managerApi.getPontoLog(), managerApi.getAlimLog()]);
    let body = `Resumo do dia ${fmtDateBR(date)}\n\nPONTO:\n`;
    const byEmp: Record<string, any[]> = {};
    pontoLog.rows.filter((r) => r.date === date).forEach((r) => { (byEmp[r.nome] ||= []).push(r); });
    Object.entries(byEmp).forEach(([nome, recs]) => {
      body += `- ${nome}: ` + PUNCH_TYPES.map((p) => { const r = recs.find((x) => x.tipo === p.key); return r ? `${p.label} ${fmtHM(r.time)}` : null; }).filter(Boolean).join(' | ') + '\n';
    });
    body += `\nVALE-ALIMENTAÇÃO:\n`;
    alimLog.rows.filter((r) => r.date === date).forEach((r) => { body += `- ${r.nome}: recebido às ${fmtHM(r.hora)}\n`; });
    const mailto = `mailto:${encodeURIComponent(email || '')}?subject=${encodeURIComponent('Resumo Coala — ' + fmtDateBR(date))}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  }

  if (loading || !cfg) return <div className="card"><p className="muted">Carregando…</p></div>;

  return (
    <>
      <div className="card">
        <h2>Tipo de horário do ponto</h2>
        <p className="hint">Troca protegida por senha especial. Escolha como os horários esperados são definidos:</p>
        {Object.entries(TIPO_HORARIO_LABELS).map(([key, label]) => (
          <label key={key} className="checkrow" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <input type="radio" name="tipoHorario" checked={cfg.tipoHorario === key} disabled={key === 'manual'} onChange={() => askScheduleModePassword(key)} />
            <span>{label}{key === 'manual' ? ' — indisponível por enquanto' : ''}</span>
          </label>
        ))}
      </div>

      {cfg.tipoHorario === 'turno' && (
        <div className="card">
          <h2>Âncoras dos turnos</h2>
          <p className="hint">Defina só o horário de entrada de cada turno — os outros 3 horários (saída descanso, volta, saída) são calculados automaticamente.</p>
          <div className="row">
            <div className="col">
              <label>Entrada — Turno Diurno</label>
              <input type="time" value={diurnoEntrada} onChange={(e) => setDiurnoEntrada(e.target.value)} />
              <div className="row" style={{ marginTop: 8 }}>
                <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => previewTurno('diurno')}>Ver prévia</button>
                <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => saveTurnoAncora('diurno')}>Salvar</button>
              </div>
            </div>
            <div className="col">
              <label>Entrada — Turno Noturno</label>
              <input type="time" value={noturnoEntrada} onChange={(e) => setNoturnoEntrada(e.target.value)} />
              <div className="row" style={{ marginTop: 8 }}>
                <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => previewTurno('noturno')}>Ver prévia</button>
                <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => saveTurnoAncora('noturno')}>Salvar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Tolerância de horário (modo Esperado)</h2>
        <p className="hint">Quantos minutos antes ou depois do horário previsto contam como "no horário". Fora disso, o ponto continua sendo aceito, mas fica sinalizado e exige justificativa (não bloqueamos o registro).</p>
        <input type="number" min={0} max={60} value={tol} onChange={(e) => setTol(e.target.value)} />
        <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={saveTolerance}>Salvar tolerância</button>
      </div>

      <div className="card">
        <h2>Vale-transporte padrão</h2>
        <p className="hint">Valor e data pré-preenchidos na tela do funcionário — ele só confirma, com selfie e localização.</p>
        <div className="row">
          <div className="col"><label>Valor (R$)</label><input type="text" placeholder="ex: 20,00" value={vtValor} onChange={(e) => setVtValor(e.target.value)} /></div>
          <div className="col"><label>Data de recebimento</label><input type="date" value={vtData} onChange={(e) => setVtData(e.target.value)} /></div>
        </div>
        <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={saveVt}>Salvar padrão de VT</button>
      </div>

      <div className="card">
        <h2>Senhas do gestor</h2>
        <p className="hint">Mais de uma senha pode estar ativa ao mesmo tempo.</p>
        {passwords.map((p) => (
          <div className="kv" key={p.id}>
            <span>Senha criada em {new Date(p.criadoEm).toLocaleString('pt-BR')}</span>
            <button className="btn btn-danger btn-sm" onClick={() => removePassword(p.id)}>Remover</button>
          </div>
        ))}
        <label>Adicionar nova senha</label>
        <input type="text" placeholder="Nova senha mestre" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={addPassword}>Adicionar senha</button>
      </div>

      <div className="card">
        <h2>Camarotes</h2>
        <p className="hint">Um por linha.</p>
        <textarea style={{ minHeight: 80 }} value={camarotesText} onChange={(e) => setCamarotesText(e.target.value)} />
        <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={saveCamarotes}>Salvar camarotes</button>
      </div>

      <div className="card">
        <h2>Termo de aceite (assinatura eletrônica)</h2>
        <p className="hint">Texto exibido a cada funcionário no primeiro acesso. Ele precisa aceitar para poder bater ponto ou registrar qualquer coisa. Recomendamos revisar com um advogado antes do evento.</p>
        <textarea style={{ minHeight: 180 }} value={termo} onChange={(e) => setTermo(e.target.value)} />
        <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={saveTermo}>Salvar termo</button>
      </div>

      <div className="card">
        <h2>SMS / WhatsApp</h2>
        <p className="hint">Provedor de envio ainda não configurado. Os botões de "enviar SMS/WhatsApp" (cadastro de funcionário e lembretes) já existem e registram a tentativa, mas nada é enviado de fato até um provedor (ex: Twilio, Zenvia) ser conectado.</p>
      </div>

      <div className="card">
        <h2>Enviar resumo por e-mail</h2>
        <p className="hint">O sistema não envia e-mails automaticamente — isso abre seu app de e-mail com um resumo já escrito. Para dados completos, use "Exportar CSV" nas abas Ponto, Alimentação e Recebimentos e anexe manualmente.</p>
        <label>Seu e-mail (gestor)</label>
        <input type="text" placeholder="voce@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button className="btn btn-ghost" style={{ marginTop: 10 }} onClick={saveEmail}>Salvar e-mail</button>
        <button className="btn btn-gold btn-block" style={{ marginTop: 10 }} onClick={sendSummaryEmail}>Gerar resumo e abrir e-mail</button>
      </div>

      <div className="card" style={{ border: '1.5px solid var(--coral)' }}>
        <h2 style={{ color: 'var(--coral)' }}>Zona de perigo — excluir funcionários</h2>
        <p className="hint">Apaga o cadastro e TODOS os registros do(s) funcionário(s) escolhido(s): ponto, alimentação, recebimentos, documentos assinados, assinatura, fotos. <b>Não pode ser desfeito.</b> Considere baixar um backup antes (aba Documentação).</p>

        <label className="checkrow" style={{ marginTop: 10 }}>
          <input type="radio" name="modoExclusao" checked={modoExclusao === 'selecionar'} onChange={() => setModoExclusao('selecionar')} /> Selecionar quais excluir
        </label>
        <label className="checkrow">
          <input type="radio" name="modoExclusao" checked={modoExclusao === 'todos'} onChange={() => setModoExclusao('todos')} /> Excluir todos os funcionários ({employees.length})
        </label>

        {modoExclusao === 'selecionar' && (
          <div style={{ marginTop: 10 }}>
            <div className="toolbar">
              <button className="btn btn-ghost btn-sm" onClick={toggleTodosSelecionados}>{selecionados.size === employees.length ? 'Desmarcar todos' : 'Selecionar todos'}</button>
              <span className="muted">{selecionados.size} selecionado(s)</span>
            </div>
            {employees.map((e) => (
              <label className="checkrow" style={{ marginTop: 6 }} key={e.id}>
                <input type="checkbox" checked={selecionados.has(e.id)} onChange={() => toggleSelecionado(e.id)} /> {e.nome} <span className="muted">— {e.camarote}</span>
              </label>
            ))}
          </div>
        )}

        <label style={{ marginTop: 14 }}>Digite EXCLUIR para confirmar</label>
        <input type="text" placeholder="EXCLUIR" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
        <label>Senha</label>
        <input type="password" value={senhaExcluir} onChange={(e) => setSenhaExcluir(e.target.value)} />
        {erroExclusao && <div className="err">{erroExclusao}</div>}
        <button className="btn btn-danger btn-block" style={{ marginTop: 14 }} disabled={excluindo} onClick={excluirFuncionarios}>
          Excluir {qtdAlvo > 0 ? `${qtdAlvo} funcionário(s)` : 'funcionários'} definitivamente
        </button>
      </div>
    </>
  );
}
